import { useMemo } from 'react'

import { Button, colors, CSSObject, styled, Theme, Typography } from '@mui/material'
import { DistributedTaskLogEntry, DistributedTaskLogEntryTypeEnum } from '@nativewaves/nw-cloud-sdk/smep'
import { useTranslation } from 'react-i18next'

import { ThemeTypes } from 'stores/settings'
import { getLogEntryLevelColor } from 'utils'

const sharedStyles = ({ theme, checked }: { theme: Theme; checked?: boolean }): CSSObject => ({
  minWidth: 80,
  textAlign: 'center',
  display: 'inline-block',
  padding: theme.spacing(0.3),
  margin: theme.spacing(0),
  textTransform: 'none',
  fontWeight: 400,
  borderRadius: 4,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  transition: '0.2s ease-in-out all',
  textDecoration: checked ? 'underline' : 'none',
  backgroundColor: theme.palette.mode === ThemeTypes.LIGHT ? theme.palette.grey[200] : theme.palette.grey[800],
  color: theme.palette.getContrastText(
    theme.palette.mode === ThemeTypes.LIGHT ? theme.palette.grey[200] : theme.palette.grey[800],
  ),
  '&:hover': {
    textDecoration: 'underline',
    opacity: 0.8,
  },
})

const TypeVisualizer = styled(Button)<{ checked: boolean }>(({ theme, checked }) => ({
  ...sharedStyles({ theme, checked }),
}))

const AlertIdVisualizer = styled(Button)<{ checked: boolean }>(({ theme, checked }) => ({
  ...sharedStyles({ theme, checked }),
}))

const AlertCodeVisualizer = styled(Button)<{ checked: boolean }>(({ theme, checked }) => ({
  ...sharedStyles({ theme, checked }),
  width: 'auto',
}))

const StatusVisualizer = styled(Button)<{ checked: boolean; isSuccess: boolean }>(({ theme, checked, isSuccess }) => ({
  ...sharedStyles({ theme, checked }),
  width: 'auto',
  backgroundColor: isSuccess
    ? theme.palette.mode === ThemeTypes.LIGHT
      ? colors.green[300]
      : colors.green[800]
    : theme.palette.mode === ThemeTypes.LIGHT
      ? theme.palette.grey[200]
      : theme.palette.grey[800],
  color: theme.palette.getContrastText(
    isSuccess
      ? theme.palette.mode === ThemeTypes.LIGHT
        ? colors.green[300]
        : colors.green[800]
      : theme.palette.mode === ThemeTypes.LIGHT
        ? theme.palette.grey[200]
        : theme.palette.grey[800],
  ),
}))

type StatusVisualizerButtonProps = {
  logType: DistributedTaskLogEntry['type']
  onClick: (types: DistributedTaskLogEntryTypeEnum[]) => void
  checked: boolean
}

export const StatusVizualizerButton = ({ logType, onClick, checked }: StatusVisualizerButtonProps) => {
  const { t } = useTranslation(['domain'])

  if (logType !== 'AlertCreation' && logType !== 'AlertDeletion') return null

  const typeObject = {
    AlertCreation: {
      values: [DistributedTaskLogEntryTypeEnum.AlertCreation],
      label: t('domain:Workspace.Event.new'),
      success: false,
    },
    AlertDeletion: {
      values: [DistributedTaskLogEntryTypeEnum.AlertDeletion],
      label: t('domain:Workspace.Event.resolved'),
      success: true,
    },
  }

  return (
    <StatusVisualizer
      onClick={(e) => {
        e.stopPropagation()
        onClick(typeObject[logType].values)
      }}
      isSuccess={typeObject[logType].success}
      checked={checked}
    >
      <Typography variant="text-sm">{typeObject[logType].label}</Typography>
    </StatusVisualizer>
  )
}

type LevelVisualizerButtonProps = {
  level: DistributedTaskLogEntry['level']
  checked: boolean
}

export const LevelVisualizerButton = styled(Button)<LevelVisualizerButtonProps>(({ theme, level, checked }) => ({
  ...sharedStyles({ theme, checked }),
  backgroundColor: getLogEntryLevelColor(level, theme.palette.mode),
  color: theme.palette.getContrastText(getLogEntryLevelColor(level, theme.palette.mode)),
}))

type TypeVisualizerButtonProps = {
  logType: DistributedTaskLogEntry['type'] | 'Alert'
  onClick: (types: DistributedTaskLogEntryTypeEnum[]) => void
  checked: boolean
}

export const TypeVizualizerButton = ({ logType, onClick, checked }: TypeVisualizerButtonProps) => {
  const { t } = useTranslation(['domain'])

  const typeObject = useMemo(() => {
    switch (logType) {
      case 'Log':
        return {
          values: [DistributedTaskLogEntryTypeEnum.Log],
          label: t('Workspace.Event.log', { count: 1 }),
        }
      case 'AlertCreation':
      case 'AlertDeletion':
        return {
          values: [DistributedTaskLogEntryTypeEnum.AlertCreation, DistributedTaskLogEntryTypeEnum.AlertDeletion],
          label: t('Workspace.Event.alert', { count: 1 }),
        }
      case 'AlertUpdate':
        return {
          values: [
            DistributedTaskLogEntryTypeEnum.AlertUpdate,
            DistributedTaskLogEntryTypeEnum.AlertCreation,
            DistributedTaskLogEntryTypeEnum.AlertDeletion,
          ],
          label: t('Workspace.Event.alertUpdate'),
        }
      // errors case
      case 'Alert':
        return {
          values: [
            DistributedTaskLogEntryTypeEnum.AlertCreation,
            DistributedTaskLogEntryTypeEnum.AlertDeletion,
            DistributedTaskLogEntryTypeEnum.AlertUpdate,
          ],
          label: t('Workspace.Event.alert', { count: 1 }),
        }
      default:
        return {
          values: [logType],
          label: logType,
        }
    }
  }, [logType, t])

  return (
    <TypeVisualizer
      onClick={(e) => {
        e.stopPropagation()
        onClick(typeObject.values)
      }}
      checked={checked}
    >
      <Typography variant="text-sm">{typeObject.label}</Typography>
    </TypeVisualizer>
  )
}

type AlertIdBadgeProps = {
  alertId: DistributedTaskLogEntry['alertId']
  onClick: (alertId: string) => void
  checked: boolean
}

export const AlertIdBadge = ({ alertId, onClick, checked }: AlertIdBadgeProps) => {
  if (!alertId) return null

  return (
    <AlertIdVisualizer
      onClick={(e) => {
        e.stopPropagation()
        onClick(alertId)
      }}
      checked={checked}
    >
      <Typography variant="text-sm">{alertId.slice(0, 8)}</Typography>
    </AlertIdVisualizer>
  )
}

type AlertCodeBadgeProps = {
  alertCode: DistributedTaskLogEntry['alertCode']
  description: DistributedTaskLogEntry['message']
  checked: boolean
  onCodeClick: (alertCode: string) => void
}

export const DescriptionVizualizer = ({ alertCode, description, onCodeClick, checked }: AlertCodeBadgeProps) => (
  <Typography variant="text" sx={{ wordWrap: 'break-word' }}>
    {alertCode ? (
      <>
        <AlertCodeVisualizer
          onClick={(e) => {
            e.stopPropagation()
            onCodeClick(alertCode)
          }}
          sx={{ flexShrink: 0 }}
          checked={checked}
        >
          <Typography variant="text-sm">{alertCode}</Typography>
        </AlertCodeVisualizer>
        {': '}
      </>
    ) : null}
    {description}
  </Typography>
)
